/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	ChangeDetectorRef,
	Component,
	OnInit,
	SecurityContext
} from '@angular/core';
import {
	DomSanitizer
} from '@angular/platform-browser';
import {
	FieldType
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	ActivityService
} from '@shared/services/activity.service';

@Component({
	selector: 'custom-text-display',
	templateUrl: './custom-text-display.component.html',
	styleUrls: [
		'./custom-text-display.component.scss'
	],
})

/**
 * A component representing an instance of a Custom Text Display.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomTextDisplayComponent
 * @extends {FieldType}
 */
export class CustomTextDisplayComponent
	extends FieldType
	implements OnInit
{
	/**
	 * Initializes a new instance of the ManageUserComponent class.
	 *
	 * @param {DomSanitizer} sanitizer
	 * The sanitizer used to ensure safe inner html and inner text displays.
	 * @param {ActivityService} activityService
	 * The activity service used to handle data interactions and client
	 * messaging.
	 * @param {ChangeDetectorRef} changeDetectorReference
	 * The change detector reference for this component.
	 * @memberof ManageUserComponent
	 */
	public constructor(
		public sanitizer: DomSanitizer,
		public activityService: ActivityService,
		public changeDetectorReference: ChangeDetectorRef)
	{
		super();
	}

	/**
	 * Gets or sets the text content to display.
	 *
	 * @type {string}
	 * @memberof CustomTextDisplayComponent
	 */
	public content: string;

	/**
	 * Gets or sets if content has been copied to clipboard.
	 *
	 * @type {boolean}
	 * @memberof CustomTextDisplayComponent
	 */
	public copiedToClipboard: boolean = false;

	/**
	 * Gets or sets the loading state.
	 *
	 * @type {boolean}
	 * @memberof CustomTextDisplayComponent
	 */
	public loading: boolean = true;

	/**
	 * Gets the display time for the copied message when copying content
	 * to the clipboard.
	 *
	 * @type {number}
	 * @memberof CustomTextDisplayComponent
	 */
	private readonly copyToClipboardDisplayTime: number = 3000;

	/**
	 * Implements the on initialization interface.
	 * This will set the content to display in this text
	 * display component.
	 *
	 * @async
	 * @memberof CustomTextDisplayComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		await this.setContent();
		this.field.props.reloadTextDisplay =
			this.onTextDisplayReload.bind(this);
		this.loading = false;
		this.changeDetectorReference.detectChanges();
	}

	/**
	 * Sets the text display content.
	 *
	* @async
	 * @memberof CustomTextDisplayComponent
	 */
	public async setContent(): Promise<void>
	{
		if (!AnyHelper.isNullOrWhitespace(
			this.field.props.contentPromise))
		{
			this.content =
				await StringHelper.transformToFieldDataPromise(
					this.field.props.contentPromise,
					this.field.props.context,
					this.field);

			return;
		}

		this.content =
			this.sanitizer.sanitize(
				SecurityContext.HTML,
				this.sanitizer.bypassSecurityTrustHtml(
					this.field.props.useCodeBlock === true
						? this.getCodeBlock()
						: this.field.props.content));
	}

	/**
	 * The text display reload handler.
	 *
	 * @param {boolean} [showLoading=true]
	 * A truthy defining if the loading state should be shown.
	 * 
	 * @memberof CustomTextDisplayComponent
	 */
	public onTextDisplayReload(
		showLoading: boolean = true): void
	{
		this.loading = showLoading;
		setTimeout(
			() =>
			{
				this.setContent();
				this.loading = false;
			},
			AppConstants.time.twentyFiveMilliseconds);
	}

	/**
	 * Gets the code block formatting.
	 *
	 * @returns {string}
	 * The code block.
	 * @memberof CustomTextDisplayComponent
	 */
	public getCodeBlock(): string
	{
		return `\`\`\`${this.field.props.codeBlockType}\n\r`
			+ `${this.field.props.content}\n\r\`\`\``;
	}

	/**
	 * A Function that determines content returned from the
	 * determined source (text/markdown) is null
	 *
	 * @returns {boolean}
	 * A truthy defining if the content view has content to display.
	 * @memberof CustomTextDisplayComponent
	 */
	public hasContent(): boolean
	{
		return !AnyHelper.isNullOrWhitespace(this.content);
	}

	/**
	 * Will copy the text content to clipboard.
	 *
	 * @async
	 * @memberof CustomTextDisplayComponent
	 */
	public async copyToClipboard(text: string): Promise<void>
	{
		await StringHelper
			.copyToClipboard(
				text);
		this.copiedToClipboard = true;
		this.changeDetectorReference.detectChanges();

		setTimeout(
			() =>
			{
				this.copiedToClipboard = false;
				this.changeDetectorReference.detectChanges();
			},
			this.copyToClipboardDisplayTime);
	}
}